import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { ExaminationFinding } from '../references.component';

export interface FindingReportDialogData {
  finding: ExaminationFinding;
  environmentLabel: string;
  projectId: string;
  builtAt: string;
  edgesExamined: number;
}

export interface FindingReportDialogResult {
  analysis: string;
  rootCause: string;
}

export const ROOT_CAUSE_CATEGORIES = [
  {
    value: 'transcript-hallucination',
    label: 'Transcript hallucination',
    description: 'Page ingestion LLM extracted text incorrectly',
  },
  {
    value: 'edge-misclassification',
    label: 'Edge misclassification',
    description: 'Cross-reference graph linked wrong pages/type',
  },
  {
    value: 'llm-reasoning-error',
    label: 'LLM reasoning error',
    description: 'Cross-examination LLM incorrectly classified finding',
  },
  { value: 'prompt-gap', label: 'Prompt gap', description: "Prompt doesn't handle a specific case" },
  { value: 'missing-context', label: 'Missing context', description: 'Transcript truncation removed relevant content' },
  { value: 'other', label: 'Other / Unsure', description: 'Does not fit the above categories' },
];

/**
 * Dialog for reporting a cross-examination finding as a GitHub issue.
 * Collects a user analysis summary and root cause category, then returns
 * a {@link FindingReportDialogResult} on close.
 */
@Component({
  selector: 'app-finding-report-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
  ],
  templateUrl: './finding-report-dialog.component.html',
  styleUrls: ['./finding-report-dialog.component.scss'],
})
export class FindingReportDialogComponent {
  readonly dialogRef = inject(MatDialogRef<FindingReportDialogComponent>);
  readonly data: FindingReportDialogData = inject(MAT_DIALOG_DATA);
  readonly rootCauseCategories = ROOT_CAUSE_CATEGORIES;

  analysis = '';
  rootCause = 'transcript-hallucination';

  get isSubmitDisabled(): boolean {
    return !this.analysis.trim();
  }

  submit(): void {
    if (this.isSubmitDisabled) return;
    const result: FindingReportDialogResult = {
      analysis: this.analysis.trim(),
      rootCause: this.rootCause,
    };
    this.dialogRef.close(result);
  }
}
